import React from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle, LogOut, Sparkles, PlugZap, Zap,
  Smartphone, LayoutDashboard, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";

/**
 * Sidebar — left rail of AppShell.
 *
 * Desktop (md+): permanent left column, width toggles via `collapsed`.
 * Mobile (< md): hidden by default, slides in as an overlay drawer when
 * `mobileOpen` is true. A backdrop button dismisses it.
 */
export default function Sidebar({
  collapsed, setCollapsed, screen, setScreen, user, isAdmin,
  primaryNav, secondaryNav, onLogout, mobileOpen, onCloseMobile,
}) {
  // Static class strings so Tailwind JIT can see them (dynamic string
  // concatenation into class names is a common footgun with the JIT).
  const desktopWidth = collapsed ? "md:w-[68px]" : "md:w-[248px]";
  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="md:hidden fixed inset-0 z-40 bg-black/40"
          onClick={onCloseMobile}
          data-testid="app-sidebar-backdrop"
        />
      )}
      <aside
        className={[
          mobileOpen
            ? "flex fixed inset-y-0 left-0 z-50 w-72"
            : "hidden",
          "md:flex md:relative md:inset-auto md:z-auto",
          desktopWidth,
          "shrink-0 h-full border-r border-[var(--rammy-border)] bg-[var(--rm-surface)] flex-col transition-[width] duration-200",
        ].join(" ")}
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        {/* Brand — when expanded shows logo + collapse toggle side by side.
            When COLLAPSED the toggle floats top-center and the logo becomes
            the entire brand affordance; keeping both in a `justify-between`
            row at 68px wide caused the toggle to visually overlap the logo. */}
        <div className={`h-14 border-b border-[var(--rammy-border)] flex items-center ${collapsed ? "px-0 justify-center relative" : "px-3 justify-between"}`}>
          <Link to="/" className={`flex items-center gap-2.5 min-w-0 ${collapsed ? "" : ""}`} data-testid="sidebar-logo">
            <div className="rammy-orb rammy-orb--sm" data-mood="idle" aria-hidden />
            {!collapsed && <span className="font-display text-lg truncate">Rammy</span>}
          </Link>
          {!collapsed && (
            <button
              onClick={() => setCollapsed(c => !c)}
              className="text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] transition"
              data-testid="sidebar-toggle"
              aria-label="Collapse sidebar"
            >
              <PanelLeftClose size={16}/>
            </button>
          )}
        </div>
        {/* Expand toggle — floats just below the brand when collapsed so it
            never fights the logo for pixels. */}
        {collapsed && (
          <button
            onClick={() => setCollapsed(c => !c)}
            className="mx-auto mt-1 w-7 h-7 rounded-lg text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] hover:bg-[var(--rammy-surface-alt)] transition flex items-center justify-center"
            data-testid="sidebar-toggle"
            aria-label="Expand sidebar"
          >
            <PanelLeftOpen size={16}/>
          </button>
        )}

        {/* New chat pill */}
        <div className={collapsed ? "px-2 pt-2 pb-2" : "p-3"}>
          <button
            onClick={() => setScreen("chat")}
            data-testid="sidebar-new-chat"
            title={collapsed ? "Talk to Rammy" : undefined}
            className={`w-full h-10 rounded-full bg-[var(--rm-brand)] text-[var(--rm-on-brand)] hover:bg-[var(--rm-brand-hover)] active:scale-[0.98] transition-[background-color,transform] flex items-center justify-center gap-2 text-sm font-medium ${collapsed ? "px-0" : ""}`}
          >
            <MessageCircle size={15}/>
            {!collapsed && <span>Talk to Rammy</span>}
          </button>
        </div>

        {/* Primary nav */}
        <nav className="flex-1 overflow-y-auto subtle-scroll px-2 pb-3" data-testid="sidebar-nav">
          <NavGroup label={collapsed ? null : "Workspace"}>
            {primaryNav.map(item => (
              <NavItem
                key={item.id}
                item={item}
                collapsed={collapsed}
                active={screen === item.id}
                onClick={() => setScreen(item.id)}
              />
            ))}
          </NavGroup>
          <NavGroup label={collapsed ? null : "Configure"}>
            {secondaryNav.map(item => (
              <NavItem
                key={item.id}
                item={item}
                collapsed={collapsed}
                active={screen === item.id}
                onClick={() => setScreen(item.id)}
              />
            ))}
          </NavGroup>
          {isAdmin && (
            <NavGroup label={collapsed ? null : "Admin"}>
              <Link to="/admin" data-testid="sidebar-admin-link">
                <SidebarButton icon={<LayoutDashboard size={18}/>} label="Admin dashboard" collapsed={collapsed}/>
              </Link>
              <Link to="/app/preview" data-testid="sidebar-preview-link">
                <SidebarButton icon={<Smartphone size={18}/>} label="Mobile app preview" collapsed={collapsed}/>
              </Link>
            </NavGroup>
          )}
        </nav>

        {/* User footer */}
        <div className={`border-t border-[var(--rammy-border)] ${collapsed ? "p-2" : "p-3"}`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-9 h-9 rounded-xl bg-[var(--rammy-cyan)]/20 flex items-center justify-center text-sm font-semibold" data-testid="sidebar-avatar" title={user?.email}>
                {(user?.name || user?.email || "N")[0]?.toUpperCase()}
              </div>
              <button
                onClick={onLogout}
                className="w-7 h-7 rounded-lg text-[var(--rammy-text-muted)] hover:text-red-500 hover:bg-[var(--rammy-surface-alt)] transition flex items-center justify-center"
                aria-label="Sign out"
                data-testid="sidebar-logout"
                title="Sign out"
              >
                <LogOut size={14}/>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-[var(--rammy-cyan)]/20 flex items-center justify-center text-sm font-semibold shrink-0" data-testid="sidebar-avatar">
                {(user?.name || user?.email || "N")[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
                <div className="text-[11px] text-[var(--rammy-text-muted)] truncate">
                  {user?.plan?.toUpperCase() || "FREE"} · {user?.email}
                </div>
              </div>
              <button
                onClick={onLogout}
                className="text-[var(--rammy-text-muted)] hover:text-red-500 transition"
                aria-label="Sign out"
                data-testid="sidebar-logout"
              >
                <LogOut size={16}/>
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

function NavGroup({ label, children }) {
  return (
    <div className="mt-3 first:mt-0">
      {label && <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold px-3 mb-1.5">{label}</div>}
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function NavItem({ item, active, collapsed, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`nav-${item.id}`}
      data-active={active ? "true" : "false"}
      title={collapsed ? item.label : undefined}
      className={`w-full flex items-center h-10 rounded-xl text-sm transition ${
        collapsed ? "justify-center px-0" : "gap-3 px-3"
      } ${
        active
          ? "bg-[var(--rammy-cyan)]/15 text-[var(--rammy-text)] font-semibold"
          : "text-[var(--rammy-text-sec)] hover:bg-[var(--rammy-surface-alt)]"
      }`}
    >
      <span className={`shrink-0 ${active ? "text-[var(--rammy-cyan-dark)]" : ""}`}>{item.icon}</span>
      {!collapsed && <span className="flex-1 text-left truncate">{item.label}</span>}
      {!collapsed && item.badge != null && item.badge > 0 && (
        <span className="text-[10px] font-mono bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)] rounded-full px-1.5 py-0.5 font-semibold">
          {item.badge}
        </span>
      )}
      {collapsed && item.badge != null && item.badge > 0 && (
        <span className="absolute ml-6 -mt-4 w-1.5 h-1.5 rounded-full bg-[var(--rammy-cyan-dark)]"/>
      )}
    </button>
  );
}

function SidebarButton({ icon, label, collapsed }) {
  return (
    <div className={`w-full flex items-center h-10 rounded-xl text-sm text-[var(--rammy-text-sec)] hover:bg-[var(--rammy-surface-alt)] transition ${collapsed ? "justify-center px-0" : "gap-3 px-3"}`}>
      <span className="shrink-0">{icon}</span>
      {!collapsed && <span className="flex-1 text-left truncate">{label}</span>}
    </div>
  );
}
